/**
 * PolicyNodeBadge Component
 *
 * Visual indicator for auto-inserted policy nodes.
 * Policies are system-managed security gates that users can see
 * but cannot add or remove.
 *
 * Badge types:
 * - PII Guard (blue): Scans for personally identifiable information
 * - Rate Limit (amber): Limits execution frequency
 * - Content Filter (purple): Filters harmful content
 *
 * The badge shows:
 * - Icon representing the policy type
 * - Short label
 * - Animated indicator when policy triggers
 */

import type { PolicyNode, PolicyType } from '@deep-agents/shared-types';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Props for PolicyNodeBadge component.
 */
interface PolicyNodeBadgeProps {
  /** The policy to display */
  policy: PolicyNode;

  /** Optional click handler (for showing details) */
  onClick?: () => void;

  /** Optional size variant */
  size?: 'sm' | 'md';
}

// ============================================================================
// POLICY CONFIGURATION
// ============================================================================

/**
 * Visual configuration for each policy type.
 * Defines icon, label, and color scheme.
 */
const POLICY_INFO: Record<PolicyType, {
  icon: string;
  label: string;
  color: string;
  description: string;
}> = {
  'pii-guard': {
    icon: '\uD83D\uDEE1\uFE0F', // Shield emoji
    label: 'PII Guard',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    description: 'Scans for personal data (emails, phone numbers, SSNs)',
  },
  'rate-limiter': {
    icon: '\u23F1\uFE0F', // Timer emoji
    label: 'Rate Limit',
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    description: 'Limits how often this operation can run',
  },
  'content-filter': {
    icon: '\uD83D\uDD0D', // Magnifying glass emoji
    label: 'Content Filter',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    description: 'Filters harmful or inappropriate content',
  },
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * PolicyNodeBadge - Displays a policy indicator badge.
 *
 * Shows the policy type with an icon and label.
 * Animates when the policy is triggered (detected an issue).
 *
 * @example
 * <PolicyNodeBadge
 *   policy={{
 *     id: 'policy-pii-guard-1',
 *     type: 'pii-guard',
 *     status: 'active',
 *     ...
 *   }}
 * />
 */
export function PolicyNodeBadge({
  policy,
  onClick,
  size = 'sm',
}: PolicyNodeBadgeProps) {
  // Get visual config for this policy type
  const info = POLICY_INFO[policy.type];

  // Size classes
  const sizeClasses = size === 'sm'
    ? 'px-2 py-0.5 text-[10px] gap-1'
    : 'px-3 py-1 text-xs gap-1.5';

  return (
    <div
      className={`
        inline-flex items-center ${sizeClasses}
        font-medium rounded-full border
        ${info.color}
        ${policy.status === 'triggered' ? 'animate-pulse' : ''}
        ${onClick ? 'cursor-pointer hover:opacity-80' : ''}
        transition-opacity
      `}
      title={`${info.label} - ${info.description}\nStatus: ${policy.status}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Policy icon */}
      <span aria-hidden="true">{info.icon}</span>

      {/* Policy label */}
      <span>{info.label}</span>

      {/* Triggered indicator - shows animated dot when policy detected an issue */}
      {policy.status === 'triggered' && (
        <span
          className="w-1.5 h-1.5 rounded-full bg-current animate-ping"
          aria-label="Policy triggered"
        />
      )}

      {/* Bypassed indicator - shows when policy is temporarily disabled */}
      {policy.status === 'bypassed' && (
        <span
          className="text-[8px] opacity-60"
          aria-label="Policy bypassed"
        >
          (off)
        </span>
      )}
    </div>
  );
}

// ============================================================================
// POLICY BADGE LIST
// ============================================================================

/**
 * Props for PolicyBadgeList component.
 */
interface PolicyBadgeListProps {
  /** Array of policies to display */
  policies: PolicyNode[];

  /** Optional click handler for policy details */
  onPolicyClick?: (policy: PolicyNode) => void;

  /** Optional size variant */
  size?: 'sm' | 'md';
}

/**
 * PolicyBadgeList - Displays multiple policy badges.
 *
 * Use this to show all policies protecting a node.
 *
 * @example
 * <PolicyBadgeList
 *   policies={[piiGuardPolicy, rateLimiterPolicy]}
 *   onPolicyClick={(policy) => showPolicyDetails(policy)}
 * />
 */
export function PolicyBadgeList({
  policies,
  onPolicyClick,
  size = 'sm',
}: PolicyBadgeListProps) {
  if (policies.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {policies.map((policy) => (
        <PolicyNodeBadge
          key={policy.id}
          policy={policy}
          size={size}
          onClick={onPolicyClick ? () => onPolicyClick(policy) : undefined}
        />
      ))}
    </div>
  );
}

// ============================================================================
// POLICY STATUS INDICATOR
// ============================================================================

/**
 * Props for PolicyStatusIndicator component.
 */
interface PolicyStatusIndicatorProps {
  /** Number of policies protecting the node */
  policyCount: number;

  /** Whether any policy is currently triggered */
  hasTriggered: boolean;

  /** Optional click handler to expand policy details */
  onClick?: () => void;
}

/**
 * PolicyStatusIndicator - Compact indicator showing policy protection status.
 *
 * Use this as a small indicator on nodes to show they have policy protection.
 * Click to expand and see full policy details.
 *
 * @example
 * <PolicyStatusIndicator
 *   policyCount={2}
 *   hasTriggered={false}
 *   onClick={() => setShowPolicies(true)}
 * />
 */
export function PolicyStatusIndicator({
  policyCount,
  hasTriggered,
  onClick,
}: PolicyStatusIndicatorProps) {
  if (policyCount === 0) {
    return null;
  }

  return (
    <button
      className={`
        inline-flex items-center gap-1 px-1.5 py-0.5
        text-[10px] font-medium rounded
        ${hasTriggered
          ? 'bg-red-100 text-red-700 animate-pulse'
          : 'bg-gray-100 text-gray-600'
        }
        hover:opacity-80 transition-opacity
      `}
      onClick={onClick}
      title={`${policyCount} security ${policyCount === 1 ? 'policy' : 'policies'} active`}
    >
      <span>\uD83D\uDEE1\uFE0F</span>
      <span>{policyCount}</span>
    </button>
  );
}
